/**
 * Instance Manager
 * 
 * Handles whirlpool and rift instance state, entry/exit logic,
 * and chunk loading per instance type.
 */

import Phaser from 'phaser';
import { Socket } from 'socket.io-client';
import { WORLD_WIDTH, WORLD_HEIGHT, UNCHARTED_WATERS_BUFFER_PX } from '../types';
import { WhirlpoolInstanceData } from '../systems/WhirlpoolRenderer';
import { playSound, SFX_KEYS } from '../systems/SoundManager';

export interface InstanceManagerCallbacks {
   getSocket: () => Socket | null;
   getLocalShip: () => Phaser.GameObjects.Container | null;
   getIslandRenderer: () => any;
   getFishingSpotRenderer: () => any;
   getPlacedTileRenderer: () => any;
   getWhirlpoolRenderer: () => any;
   getRiftScarManager: () => any;
   getPermanentLocationRenderer: () => any;
   getCameras: () => Phaser.Cameras.Scene2D.CameraManager;
   showFloatingMessage: (message: string, color: string) => void;
   suppressInput: (durationMs: number) => void;
}

export class InstanceManager {
  private scene: Phaser.Scene;
  private callbacks: InstanceManagerCallbacks;
  
  // Instance state
  private isInWhirlpoolInstance: boolean = false;
  private isInRiftInstance: boolean = false;
  private currentRiftId: string = 'core';

  constructor(scene: Phaser.Scene, callbacks: InstanceManagerCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  // ============ GETTERS/SETTERS ============

  getIsInWhirlpoolInstance(): boolean {
    return this.isInWhirlpoolInstance;
  }

  setIsInWhirlpoolInstance(isIn: boolean): void {
    this.isInWhirlpoolInstance = isIn;
  }

  getIsInRiftInstance(): boolean {
    return this.isInRiftInstance;
  }

  setIsInRiftInstance(isIn: boolean): void {
    this.isInRiftInstance = isIn;
  }

  getCurrentRiftId(): string {
    return this.currentRiftId;
  }

  setCurrentRiftId(riftId: string): void {
    this.currentRiftId = riftId;
  }

  isInAnyInstance(): boolean {
    return this.isInWhirlpoolInstance || this.isInRiftInstance;
  }

  // ============ WHIRLPOOL INSTANCE ============

  /**
   * Enter a whirlpool instance
   */
  enterWhirlpoolInstance(instance: WhirlpoolInstanceData): void {
    console.log(`🌀 Entering whirlpool instance: ${instance.className}`);
    this.isInWhirlpoolInstance = true;

    playSound(SFX_KEYS.WHIRLPOOL_ENTER);

    this.callbacks.suppressInput(350);

    // Hide overworld visuals
    this.hideOverworldVisuals();

    // Set instance camera bounds
    this.callbacks.getCameras().main.setBounds(0, 0, instance.width, instance.height);

    // Enter instance in renderer
    this.callbacks.getWhirlpoolRenderer().enterInstance(instance);
    
    // Emit event for React UI
    this.scene.events.emit('whirlpool:entered', { instance });
    
    this.callbacks.showFloatingMessage(`Entered ${instance.className}`, '#00ffff');
  }

  /**
   * Exit whirlpool instance
   */
  exitWhirlpoolInstance(): void {
    console.log(`🌀 Exiting whirlpool instance`);
    this.isInWhirlpoolInstance = false;

    playSound(SFX_KEYS.WHIRLPOOL_EXIT);
    
    this.callbacks.getWhirlpoolRenderer().exitInstance();

    // Restore overworld camera bounds
    this.restoreOverworldCameraBounds();

    // Restore overworld visuals
    this.showOverworldVisuals();
    
    // Emit event for React UI
    this.scene.events.emit('whirlpool:exited');
    
    // Re-fetch whirlpools for overworld
    const socket = this.callbacks.getSocket();
    socket?.emit('whirlpool:getAll');
    
    this.callbacks.showFloatingMessage('Returned to the ocean', '#00ffff');
  }

  /**
   * Handle whirlpool collapse (emergency exit)
   */
  handleWhirlpoolCollapse(): void {
    console.log(`🌀 Whirlpool collapsed! Emergency exit.`);
    this.isInWhirlpoolInstance = false;

    playSound(SFX_KEYS.WHIRLPOOL_EXIT);
    this.callbacks.getWhirlpoolRenderer().exitInstance();
    
    this.restoreOverworldCameraBounds();
    this.showOverworldVisuals();
    
    this.scene.events.emit('whirlpool:exited');
    
    const socket = this.callbacks.getSocket();
    socket?.emit('whirlpool:getAll');
    
    this.callbacks.showFloatingMessage('WHIRLPOOL COLLAPSED - Emergency exit!', '#ff4444');
  }

  /**
   * Restore to whirlpool instance (on reconnect)
   */
  restoreWhirlpoolInstance(instance: WhirlpoolInstanceData): void {
    console.log(`🌀 Restored to whirlpool instance: ${instance.className}`);
    this.isInWhirlpoolInstance = true;

    playSound(SFX_KEYS.WHIRLPOOL_ENTER);

    this.hideOverworldVisuals();

    this.callbacks.getCameras().main.setBounds(0, 0, instance.width, instance.height);
    this.callbacks.getWhirlpoolRenderer().enterInstance(instance);
    
    this.scene.events.emit('whirlpool:entered', { instance });
    this.callbacks.showFloatingMessage(`Restored to ${instance.className}`, '#00ffff');
  }

  // ============ RIFT INSTANCE ============

  /**
   * Enter a rift instance
   */
  async enterRiftInstance(riftId: string, spawnX: number, spawnY: number): Promise<void> {
    console.log(`🌀 Entering rift instance: ${riftId}`);
    this.isInRiftInstance = true;
    this.currentRiftId = riftId;

    playSound(SFX_KEYS.RIFT_ENTER);
    
    this.callbacks.suppressInput(350);
    
    // Hide overworld visuals
    this.hideOverworldVisuals();
    
    // Notify PermanentLocationRenderer to clear permanent locations
    const permanentRenderer = this.callbacks.getPermanentLocationRenderer();
    if (permanentRenderer) {
      permanentRenderer.setIsInRiftInstance(true);
    }
    
    // Load rift scars for this rift
    const riftScarManager = this.callbacks.getRiftScarManager();
    if (riftScarManager) {
      await riftScarManager.loadScarsForRift(riftId);
    }
    
    // Update local ship position
    const localShip = this.callbacks.getLocalShip();
    if (localShip) {
      localShip.x = spawnX;
      localShip.y = spawnY;
    }
    
    // Center camera on new position
    this.callbacks.getCameras().main.centerOn(spawnX, spawnY);
    
    // Emit event for React UI
    this.scene.events.emit('rift:entered', { 
      riftId,
      position: { x: spawnX, y: spawnY }
    });
    
    this.callbacks.showFloatingMessage(`Entered ${riftId}`, '#00ffff');
  }

  /**
   * Exit rift instance (return to core)
   */
  exitRiftInstance(): void {
    console.log(`🌀 Exiting rift instance`);
    this.isInRiftInstance = false;
    this.currentRiftId = 'core';

    playSound(SFX_KEYS.RIFT_EXIT);
    
    this.restoreOverworldCameraBounds();
    this.showOverworldVisuals();
    
    // Notify PermanentLocationRenderer to restore permanent locations
    const permanentRenderer = this.callbacks.getPermanentLocationRenderer();
    if (permanentRenderer) {
      permanentRenderer.setIsInRiftInstance(false);
    }
    
    this.scene.events.emit('rift:exited');
    this.callbacks.showFloatingMessage('Returned to the core', '#00ffff');
  }

  /**
   * Restore to rift instance (on reconnect)
   */
  async restoreRiftInstance(riftId: string, position: { x: number; y: number }): Promise<void> {
    console.log(`🌀 Restored to rift ${riftId} at (${position.x}, ${position.y})`);
    
    this.isInRiftInstance = true;
    this.currentRiftId = riftId;

    playSound(SFX_KEYS.RIFT_ENTER);
    
    this.hideOverworldVisuals();
    
    // Notify PermanentLocationRenderer to clear permanent locations
    const permanentRenderer = this.callbacks.getPermanentLocationRenderer();
    if (permanentRenderer) {
      permanentRenderer.setIsInRiftInstance(true);
    }
    
    const riftScarManager = this.callbacks.getRiftScarManager();
    if (riftScarManager) {
      await riftScarManager.loadScarsForRift(riftId);
    }
    
    this.callbacks.getCameras().main.centerOn(position.x, position.y);
    
    this.scene.events.emit('rift:entered', { 
      riftId,
      position
    });
    
    this.callbacks.showFloatingMessage(`Restored to ${riftId}`, '#00ffff');
  }

  // ============ HELPER METHODS ============

  /**
   * Hide overworld visuals (islands, fishing spots)
   * For rifts, we destroy them completely since rift chunks replace them
   */
  private hideOverworldVisuals(): void {
    const islandRenderer = this.callbacks.getIslandRenderer();
    if (islandRenderer) {
      // For rifts, destroy islands completely (they'll be replaced by rift chunks)
      if (this.isInRiftInstance) {
        islandRenderer.clearIslands();
      } else {
        // For whirlpools, just hide them (they're temporary instances)
        islandRenderer.getIslands().forEach((entry: any) => {
          entry.graphics.setVisible(false);
          entry.hitArea?.setVisible(false);
        });
      }
    }
    
    const fishingRenderer = this.callbacks.getFishingSpotRenderer();
    if (fishingRenderer) {
      fishingRenderer.getSpots().forEach((spot: any) => {
        spot.graphics.setVisible(false);
        spot.hitArea.setVisible(false);
      });
    }
  }

  /**
   * Show overworld visuals (islands, fishing spots)
   */
  private showOverworldVisuals(): void {
    const islandRenderer = this.callbacks.getIslandRenderer();
    if (islandRenderer) {
      islandRenderer.getIslands().forEach((entry: any) => {
        entry.graphics.setVisible(true);
        entry.hitArea?.setVisible(true);
      });
    }
    
    const fishingRenderer = this.callbacks.getFishingSpotRenderer();
    if (fishingRenderer) {
      fishingRenderer.getSpots().forEach((spot: any) => {
        spot.graphics.setVisible(true);
        spot.hitArea.setVisible(true);
      });
    }
  }

  /**
   * Restore overworld camera bounds
   */
  private restoreOverworldCameraBounds(): void {
    this.callbacks.getCameras().main.setBounds(
      -UNCHARTED_WATERS_BUFFER_PX,
      -UNCHARTED_WATERS_BUFFER_PX,
      WORLD_WIDTH + UNCHARTED_WATERS_BUFFER_PX * 2,
      WORLD_HEIGHT + UNCHARTED_WATERS_BUFFER_PX * 2
    );
  }
}
